import React, { useEffect, useState } from "react";
import { getFirestore, doc, getDoc, setDoc } from "firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { auth } from "../../../firebaseConfig";
import app from "../../../firebaseConfig";
import toast from "react-hot-toast";

/**
 * CompanyProfile with resumable upload + progress
 * - Upload path: company-logos/{companyKey}/{timestamp}-{filename}
 * - After upload completes, writes companies/{companyKey}.logoUrl and logoPath to Firestore
 * - Deletes previous logo file after successful replacement
 *
 * Usage: put this at src/pages/company/CompanySettings/CompanyProfile.jsx
 */

const DEFAULT_BUCKET = "gs://neuracraft-378a2.firebasestorage.app";

const CompanyProfile = ({ companyKey: companyKeyProp }) => {
  const firestore = getFirestore(app);
  // If your firebaseConfig already includes storageBucket, getStorage(app) will use it.
  // Otherwise we create the storage instance for your known bucket.
  const storage = (() => {
    try {
      return getStorage(app);
    } catch (e) {
      return getStorage(app, DEFAULT_BUCKET);
    }
  })();

  const [resolvedCompanyKey, setResolvedCompanyKey] = useState(companyKeyProp || "");
  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState(null);

  const [form, setForm] = useState({
    companyName: "",
    email: "",
    phone: "",
    address: "",
    registrationNumber: "",
    industry: "",
    defaultCurrency: "KES",
  });

  // Upload states
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  // Resolve companyKey from prop or current signed-in user's Firestore users/{uid}.companyName
  useEffect(() => {
    if (companyKeyProp) {
      setResolvedCompanyKey(companyKeyProp);
      return;
    }
    const unsub = auth.onAuthStateChanged(async (u) => {
      if (!u) {
        setResolvedCompanyKey("");
        return;
      }
      try {
        const userRef = doc(firestore, "users", u.uid);
        const userSnap = await getDoc(userRef);
        const data = userSnap.exists() ? userSnap.data() : null;
        setResolvedCompanyKey(data?.companyName || "");
      } catch (err) {
        console.error("resolve companyKey", err);
        setResolvedCompanyKey("");
      }
    });
    return () => unsub();
  }, [companyKeyProp, firestore]);

  // Load company profile when resolvedCompanyKey is available
  useEffect(() => {
    if (!resolvedCompanyKey) {
      setCompany(null);
      setLoading(false);
      return;
    }
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const docRef = doc(firestore, "companies", resolvedCompanyKey);
        const snap = await getDoc(docRef);
        if (!mounted) return;
        if (snap.exists()) {
          const data = snap.data();
          setCompany(data);
          setForm({
            companyName: data.companyName || resolvedCompanyKey,
            email: data.email || "",
            phone: data.phone || "",
            address: data.address || "",
            registrationNumber: data.registrationNumber || "",
            industry: data.industry || "",
            defaultCurrency: data.defaultCurrency || "KES",
          });
        } else {
          setCompany(null);
        }
      } catch (err) {
        console.error("load company", err);
        toast.error("Failed to load company profile");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [resolvedCompanyKey, firestore]);

  // Upload helper using uploadBytesResumable
  const uploadLogoResumable = (file, onProgress) => {
    if (!file || !resolvedCompanyKey) return Promise.reject(new Error("Missing file or companyKey"));
    const safeName = file.name.replace(/\s+/g, "_");
    const path = `company-logos/${resolvedCompanyKey}/${Date.now()}-${safeName}`;
    const sRef = storageRef(storage, path);
    const metadata = {
      contentType: file.type || "image/jpeg",
      cacheControl: "public, max-age=31536000",
    };

    return new Promise((resolve, reject) => {
      const task = uploadBytesResumable(sRef, file, metadata);
      task.on(
        "state_changed",
        (snapshot) => {
          const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          onProgress && onProgress(pct);
        },
        (err) => reject(err),
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            resolve({ url, storagePath: path });
          } catch (err) {
            reject(err);
          }
        }
      );
    });
  };

  // Optionally delete previous logo object in Storage
  const deletePreviousLogoIfAny = async (previousPath) => {
    if (!previousPath) return;
    try {
      const prevRef = storageRef(storage, previousPath);
      await deleteObject(prevRef);
    } catch (err) {
      // non-fatal: log and continue
      console.warn("delete previous logo failed", err.message || err);
    }
  };

  // Save profile (and optionally upload new logo)
  const handleSave = async (e) => {
    e && e.preventDefault();
    if (!resolvedCompanyKey) {
      toast.error("No company selected");
      return;
    }
    setSaving(true);

    let newLogo = null;
    let newLogoPath = null;
    const previousPath = company?.logoPath || null;

    try {
      if (selectedFile) {
        setUploading(true);
        setUploadProgress(0);
        const result = await uploadLogoResumable(selectedFile, (pct) => setUploadProgress(pct));
        newLogo = result.url;
        newLogoPath = result.storagePath;
        setUploading(false);
      }

      const companyRef = doc(firestore, "companies", resolvedCompanyKey);
      const payload = {
        ...company,
        companyName: form.companyName,
        email: form.email || "",
        phone: form.phone || "",
        address: form.address || "",
        registrationNumber: form.registrationNumber || "",
        industry: form.industry || "",
        defaultCurrency: form.defaultCurrency || "KES",
        logoUrl: newLogo || company?.logoUrl || null,
        logoPath: newLogoPath || company?.logoPath || null,
        updatedAt: Date.now(),
      };

      await setDoc(companyRef, payload, { merge: true });

      // delete previous file only after new logo is persisted
      if (newLogoPath && previousPath && previousPath !== newLogoPath) {
        await deletePreviousLogoIfAny(previousPath);
      }

      toast.success("Company profile updated");
      setCompany(payload);
      setEditing(false);
      setSelectedFile(null);
      setUploadProgress(0);
    } catch (err) {
      console.error("save company", err);
      toast.error(err?.message || "Failed to save profile");
    } finally {
      setUploading(false);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (company) {
      setForm({
        companyName: company.companyName || resolvedCompanyKey,
        email: company.email || "",
        phone: company.phone || "",
        address: company.address || "",
        registrationNumber: company.registrationNumber || "",
        industry: company.industry || "",
        defaultCurrency: company.defaultCurrency || "KES",
      });
    } else {
      setForm({
        companyName: "",
        email: "",
        phone: "",
        address: "",
        registrationNumber: "",
        industry: "",
        defaultCurrency: "KES",
      });
    }
    setSelectedFile(null);
    setUploadProgress(0);
    setEditing(false);
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-slate-100 h-28 rounded" />
    );
  }

  if (!resolvedCompanyKey) {
    return (
      <div className="bg-white p-6 rounded-lg border shadow-sm text-center">
        <h3 className="text-lg font-semibold text-cyan-700 mb-2">No company selected</h3>
        <p className="text-sm text-gray-600 mt-2">We couldn't find a company for your account.</p>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-white to-slate-50 p-6 rounded-lg shadow-lg border">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-cyan-100 to-indigo-50 flex items-center justify-center border overflow-hidden">
            {company?.logoUrl ? (
              <img src={company.logoUrl} alt="logo" className="w-full h-full object-cover" />
            ) : (
              <div className="text-indigo-600 font-bold text-xl">C</div>
            )}
          </div>
        </div>

        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold">{company?.companyName || resolvedCompanyKey}</h3>
              <p className="text-sm text-gray-500 mt-1">{company?.industry || "Dairy · Processing · Distribution"}</p>
            </div>

            {/* Edit toggle */}
            <div>
              {!editing ? (
                <button
                  aria-label="Edit company profile"
                  title="Edit profile"
                  onClick={() => setEditing(true)}
                  className="p-2 rounded-lg hover:bg-slate-100 transition"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-600" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                    <path fillRule="evenodd" d="M2 15.5A1.5 1.5 0 013.5 14h1A1.5 1.5 0 016 15.5v1A1.5 1.5 0 014.5 18h-1A1.5 1.5 0 012 16.5v-1z" clipRule="evenodd" />
                  </svg>
                </button>
              ) : null}
            </div>
          </div>

          {!editing ? (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-gray-500">Email</div>
                <div className="text-sm text-slate-800">{company?.email || "—"}</div>
              </div>

              <div>
                <div className="text-xs text-gray-500">Phone</div>
                <div className="text-sm text-slate-800">{company?.phone || "—"}</div>
              </div>

              <div className="md:col-span-2">
                <div className="text-xs text-gray-500">Address</div>
                <div className="text-sm text-slate-800">{company?.address || "—"}</div>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSave} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-600">Company name</label>
                <input value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label className="block text-xs text-gray-600">Industry</label>
                <input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label className="block text-xs text-gray-600">Email</label>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label className="block text-xs text-gray-600">Phone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs text-gray-600">Address</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label className="block text-xs text-gray-600">Registration number</label>
                <input value={form.registrationNumber} onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })} className="w-full border rounded px-3 py-2" />
              </div>

              <div>
                <label className="block text-xs text-gray-600">Default currency</label>
                <select value={form.defaultCurrency} onChange={(e) => setForm({ ...form, defaultCurrency: e.target.value })} className="w-full border rounded px-3 py-2 bg-white">
                  <option value="KES">KES</option>
                  <option value="USD">USD</option>
                </select>
              </div>

              <div className="md:col-span-2 flex items-center gap-4">
                <div>
                  <label className="block text-xs text-gray-600">Logo</label>
                  <input type="file" accept="image/*" onChange={(e) => setSelectedFile(e.target.files?.[0] || null)} />
                </div>

                <div className="ml-auto flex gap-2 items-center">
                  {uploading && (
                    <div className="w-36">
                      <div className="text-xs text-gray-500 mb-1">Upload progress</div>
                      <div className="h-2 bg-slate-200 rounded overflow-hidden">
                        <div className="h-full bg-cyan-500" style={{ width: `${uploadProgress}%` }} />
                      </div>
                      <div className="text-xs text-gray-400 mt-1">{uploadProgress}%</div>
                    </div>
                  )}

                  <button type="button" onClick={handleCancel} className="px-4 py-2 border rounded">Cancel</button>
                  <button type="submit" disabled={saving || uploading} className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white rounded">
                    {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;